import { T, base } from "../lib/theme";

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    color: "#6366f1",
    pricePkr: "Rs.7,999",
    priceUsd: "$29 USD",
    priceNote: "/month",
    badge: "For solo & small businesses",
    features: [
      "600 contacts",
      "WhatsApp AI auto-reply",
      "5 broadcasts/month",
      "Knowledge base",
      "Media replies (images, PDFs)",
      "24/7 availability",
    ],
  },
  {
    id: "growth",
    name: "Growth",
    color: "#8b5cf6",
    pricePkr: "Rs.15,999",
    priceUsd: "$59 USD",
    priceNote: "/month",
    badge: "For growing businesses",
    popular: true,
    features: [
      "2,000 contacts",
      "WhatsApp AI auto-reply",
      "50 broadcasts/month",
      "Knowledge base (10x bigger)",
      "Shopify + WooCommerce",
      "AI lead generation pipeline",
      "Order auto confirmation",
      "Priority support",
    ],
  },
  {
    id: "enterprise",
    name: "Customized",
    color: "#f59e0b",
    pricePkr: "Let's talk",
    priceUsd: "Meeting-based pricing",
    priceNote: "",
    badge: "Aap ki zaroorat ke mutabiq",
    custom: true,
    features: [
      "Unlimited contacts",
      "Unlimited broadcasts",
      "Custom features",
      "Priority onboarding",
    ],
  },
];

const LIMIT_MESSAGES = {
  contacts:    { title: "Contact Limit Reached",     sub: "You've used all your contacts for this month." },
  broadcasts:  { title: "Broadcast Limit Reached",   sub: "You've reached your monthly broadcast limit."  },
  aiMessages:  { title: "AI Message Limit Reached",  sub: "You've used all your AI message credits."      },
  whatsapp:    { title: "WhatsApp Access Restricted", sub: "WhatsApp bot access is not available on your current plan." },
  broadcast:   { title: "Broadcast Not Available",   sub: "Broadcast is not included in your current plan." },
  knowledge:   { title: "Knowledge Base Locked",     sub: "Knowledge Base is not available on your plan." },
  aiInsights:  { title: "AI Insights Locked",        sub: "AI Insights is not available on your current plan." },
  pipeline:    { title: "Lead Gen Pipeline Locked",  sub: "Lead Gen Pipeline is not included in your plan." },
  shopify:     { title: "Shopify Integration Locked", sub: "Shopify integration requires a higher plan."   },
  woocommerce: { title: "WooCommerce Locked",        sub: "WooCommerce integration requires a higher plan." },
  ecomPipeline:{ title: "E-com Pipeline Locked",     sub: "E-com Pipeline requires a store connection."   },
  messenger:   { title: "Messenger Locked",          sub: "Facebook Messenger bot requires a higher-tier plan." },
  instagram:   { title: "Instagram Locked",          sub: "Instagram DMs bot requires a higher-tier plan."      },
  outbound:    { title: "Outbound Leads Locked",     sub: "Outbound Leads is not available on your plan." },
  todo:        { title: "To Do Locked",              sub: "The To Do page is not available on your plan." },
  email:       { title: "Email Assistant Locked",    sub: "Email Assistant is not available on your plan." },
  default:     { title: "Upgrade Your Plan",         sub: "Unlock more features by upgrading your plan."  },
};

const IcoCheck = ({ c }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" style={{ flexShrink: 0, marginTop: 2 }}>
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const IcoClose = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IcoZap = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
  </svg>
);

export default function UpgradeModal({ open, onClose, reason, planInfo }) {
  if (!open) return null;

  const msg = LIMIT_MESSAGES[reason] || LIMIT_MESSAGES.default;
  const currentPlan = planInfo?.plan || "free";

  const handleUpgrade = () => {
    window.open("https://zepliq.com/contact", "_blank");
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 99999,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)",
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.bg1,
          border: `1px solid ${T.border1}`,
          borderRadius: 20,
          width: "min(900px, 95vw)",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 24px 80px rgba(0,0,0,0.5)",
          animation: "toastIn 0.25s ease",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "24px 28px 0",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        }}>
          <div style={{ flex: 1 }}>
            {/* Limit icon */}
            <div style={{
              width: 44, height: 44, borderRadius: 12, marginBottom: 12,
              background: "rgba(99,102,241,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="1.8">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text1, marginBottom: 4 }}>{msg.title}</div>
            <div style={{ fontSize: 13, color: T.text3, lineHeight: 1.5 }}>{msg.sub}</div>
            {currentPlan !== "free" && (
              <div style={{ marginTop: 6, fontSize: 12, color: T.text3 }}>
                You're on the <span style={{ fontWeight: 700, color: "#6366f1" }}>{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</span> plan. Upgrade to unlock more.
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: T.bg2, border: `1px solid ${T.border1}`,
            borderRadius: 8, padding: 6, cursor: "pointer",
            color: T.text3, flexShrink: 0, marginLeft: 16,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <IcoClose />
          </button>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: T.border1, margin: "20px 0 0" }} />

        {/* Plan cards */}
        <div style={{ padding: "20px 28px 28px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 14 }}>
            Choose a plan to continue
          </div>
          <div className="grid-3" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14 }}>
            {PLANS.map(plan => {
              const isCurrent = currentPlan === plan.id;
              return (
                <div
                  key={plan.id}
                  style={{
                    padding: "20px 18px",
                    borderRadius: 16,
                    background: isCurrent ? `${plan.color}09` : T.bg2,
                    border: `2px solid ${isCurrent ? plan.color + "66" : plan.popular ? plan.color + "33" : T.border1}`,
                    position: "relative",
                    transition: "all 0.2s",
                  }}
                >
                  {plan.popular && !isCurrent && (
                    <div style={{
                      position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                      padding: "2px 12px", borderRadius: 20,
                      background: `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)`,
                      color: "#fff", fontSize: 9, fontWeight: 700, whiteSpace: "nowrap", letterSpacing: "0.05em",
                    }}>
                      MOST POPULAR
                    </div>
                  )}
                  {isCurrent && (
                    <div style={{
                      position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                      padding: "2px 12px", borderRadius: 20,
                      background: plan.color, color: "#fff", fontSize: 9, fontWeight: 700, whiteSpace: "nowrap",
                    }}>
                      CURRENT PLAN
                    </div>
                  )}

                  <div style={{ fontSize: 14, fontWeight: 800, color: plan.color, marginBottom: 2 }}>{plan.name}</div>
                  <div style={{ fontSize: 10, color: T.text3, marginBottom: 10 }}>{plan.badge}</div>

                  <div style={{ fontSize: 22, fontWeight: 900, color: T.text1, fontFamily: base.mono, marginBottom: 1 }}>
                    {plan.pricePkr} {plan.priceNote && <span style={{ fontSize: 11, color: T.text3, fontWeight: 400 }}>{plan.priceNote}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: T.text3, marginBottom: 12 }}>{plan.priceUsd}</div>

                  <div style={{ height: 1, background: T.border1, marginBottom: 12 }} />

                  <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 16 }}>
                    {plan.features.map((f, i) => (
                      <div key={i} style={{ display: "flex", gap: 7, fontSize: 11, color: T.text2, alignItems: "flex-start" }}>
                        <IcoCheck c={plan.color} />{f}
                      </div>
                    ))}
                  </div>

                  {isCurrent ? (
                    <div style={{ textAlign: "center", fontSize: 12, color: plan.color, fontWeight: 600, padding: "8px 0", borderRadius: 8, background: `${plan.color}12` }}>
                      Active plan
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      style={{
                        width: "100%", padding: "9px 0", borderRadius: 9, border: "none", cursor: "pointer",
                        fontFamily: base.font, fontSize: 12, fontWeight: 700,
                        background: `linear-gradient(135deg, ${plan.color}, ${plan.color}cc)`,
                        color: "#fff", transition: "all 0.18s",
                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                      }}
                    >
                      <IcoZap /> {plan.custom ? "Book a meeting" : `Get ${plan.name}`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 16, textAlign: "center", fontSize: 11, color: T.text3 }}>
            <a href="https://zepliq.com/contact" target="_blank" rel="noreferrer" style={{ color: "#6366f1", textDecoration: "none" }}>Contact us</a> to upgrade or ask questions about plans.
          </div>
        </div>
      </div>
    </div>
  );
}
